import { RECIPE_NAME_MAX, RECIPE_TEXT_MAX } from "./domain";

/** Versioned, provider-neutral recipe extraction contract. */
export const RECIPE_PARSE_SCHEMA_VERSION = 2;

/** Amounts within this distance of a common cooking fraction use its Unicode glyph. */
export const COOKING_FRACTION_TOLERANCE = 0.01;

/** Canonical internal unit aliases; formatted display text applies its own casing. */
export const UNIT_NORMALIZATION: Record<string, string> = {
  teaspoon: "tsp", teaspoons: "tsp", tsp: "tsp", "tsp.": "tsp",
  tablespoon: "tbsp", tablespoons: "tbsp", tbsp: "tbsp", "tbsp.": "tbsp",
  cup: "cup", cups: "cup", c: "cup", "c.": "cup",
  "fluid ounce": "fl oz", "fluid ounces": "fl oz", "fl oz": "fl oz", floz: "fl oz",
  ounce: "oz", ounces: "oz", oz: "oz", "oz.": "oz",
  pound: "lb", pounds: "lb", lb: "lb", lbs: "lb", "lb.": "lb",
  gram: "g", grams: "g", g: "g", kilogram: "kg", kilograms: "kg", kg: "kg",
  milliliter: "ml", milliliters: "ml", millilitre: "ml", millilitres: "ml", ml: "ml",
  liter: "l", liters: "l", litre: "l", litres: "l", l: "l",
  pint: "pt", pints: "pt", pt: "pt", quart: "qt", quarts: "qt", qt: "qt", gallon: "gal", gallons: "gal", gal: "gal",
  pinch: "pinch", pinches: "pinch", dash: "dash", dashes: "dash",
};

export interface ParsedIngredient {
  displayLine: string;
  amount: number | null;
  unit: string | null;
  foodName: string;
  qualifiers: string[];
}

export interface EstimatedNutrition {
  calories: number;
  protein: number;
  carbohydrates: number;
  fat: number;
}

export interface ParsedRecipe {
  version: number;
  name: string;
  servings: number;
  ingredients: ParsedIngredient[];
  instructions: string[];
  estimatedNutrition: EstimatedNutrition | null;
}

export interface RecipeParseWarning {
  code: "invalid_parse" | "incomplete_parse" | "provider_unavailable";
  message: string;
}

export const RECIPE_PARSE_JSON_SCHEMA = {
  type: "object",
  additionalProperties: false,
  required: ["version", "name", "servings", "ingredients", "instructions", "estimatedNutrition"],
  properties: {
    version: { type: "integer", minimum: RECIPE_PARSE_SCHEMA_VERSION, maximum: RECIPE_PARSE_SCHEMA_VERSION },
    name: { type: "string" },
    servings: { type: "number", exclusiveMinimum: 0 },
    ingredients: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["displayLine", "amount", "unit", "foodName", "qualifiers"],
        properties: {
          displayLine: { type: "string", description: "Untrusted source line; the app reconstructs its display text." },
          amount: { anyOf: [{ type: "number", exclusiveMinimum: 0 }, { type: "null" }] },
          unit: { anyOf: [{ type: "string" }, { type: "null" }] },
          foodName: { type: "string", description: "Canonical food name without quantity, unit, or qualifiers." },
          qualifiers: { type: "array", items: { type: "string" } },
        },
      },
    },
    instructions: { type: "array", description: "Logical, ordered cooking steps that preserve the source meaning and sequence.", items: { type: "string" } },
    estimatedNutrition: {
      anyOf: [{
        type: "object",
        additionalProperties: false,
        required: ["calories", "protein", "carbohydrates", "fat"],
        properties: {
          calories: { type: "number", minimum: 0 }, protein: { type: "number", minimum: 0 }, carbohydrates: { type: "number", minimum: 0 }, fat: { type: "number", minimum: 0 },
        },
      }, { type: "null" }],
      description: "Rough estimated totals for the entire recipe before dividing by servings.",
    },
  },
} as const;

export const RECIPE_PARSE_INSTRUCTIONS = `Extract the recipe and return only JSON with exactly these keys:
{
  "version": 2,
  "name": "recipe name",
  "servings": 1,
  "ingredients": [{ "displayLine": "original ingredient line", "amount": 1.5, "unit": "tablespoons", "foodName": "olive oil", "qualifiers": ["divided"] }],
  "instructions": ["first instruction", "second instruction"],
  "estimatedNutrition": { "calories": 1200, "protein": 80, "carbohydrates": 140, "fat": 35 }
}
Preserve every source ingredient exactly once: never drop, duplicate, combine, or invent an ingredient, amount, unit, qualifier, instruction, serving count, or health claim. The only exception is a redundant equivalent measurement on the same ingredient line. Order ingredients from greatest to least by estimated total physical quantity. Compare weight, volume, and count measurements on a common approximate scale, applying exact conversions between compatible standard units before making approximate comparisons. For count-based ingredients, estimate their typical weight or volume. Do not sort by the raw numeric value alone. Use source order only when quantities are approximately equal or cannot be reliably compared.
Format the recipe name in conventional title case, keeping articles lowercase unless they begin the title.
Correct obvious spelling, capitalization, punctuation, and grammar throughout the recipe while preserving its meaning. Never change quantities, units, temperatures, times, ingredient identities, or cooking actions as part of this cleanup.
Split paragraph-style directions into logical, ordered cooking steps. Each step should contain one coherent action or a tightly related set of actions. Preserve every instruction, its meaning, and its order; do not add, omit, or reorder actions.
Use compact Unicode cooking fractions in instructions, converting forms such as 1/2 to ½ and 1 1/2 to 1½ without changing their values.
For every ingredient supply numeric components, a canonical foodName, and qualifiers. Return exactly one amount and unit per ingredient: retain the source's primary household or count measurement and omit any equivalent trailing weight or volume conversion. For example, source text "1½ cup water, lukewarm, 340g" becomes amount 1.5, unit "cup", foodName "water", qualifiers ["lukewarm"]—never include "340g" as a qualifier or second measurement. Convert written and Unicode fractions to numeric amounts: 1 1/2 or 1½ becomes 1.5, and 2¼ becomes 2.25. Use amount 2, unit null, foodName "large eggs", qualifiers ["beaten"] for a count ingredient. Use amount null and unit null for quantity-free ingredients, for example foodName "salt", qualifiers ["to taste"]. Do not put amount or unit in foodName. The app formats examples as "1½ Tbsp olive oil, divided", "2 large eggs, beaten", "2¼ cup chicken broth", and "salt, to taste"; do not rely on displayLine punctuation or capitalization because it is reconstructed. Use an empty qualifiers array when none apply. estimatedNutrition must be totals for the complete recipe before dividing by servings. When a source omits a usable quantity, make a reasonable best guess from a typical unit (for example, the usual weight of one chicken breast) for nutrition estimation only; do not invent that quantity in the visible ingredient amount or display text. Return estimatedNutrition null only when a reasonable complete-recipe estimate genuinely cannot be made. Every ingredient must be an object and every instruction a string.`;

const MAX_ITEMS = 250;
function clean(value: unknown, max: number) { return typeof value === "string" ? value.trim().replace(/\s+/g, " ").slice(0, max) : ""; }
function amount(value: unknown) { return typeof value === "number" && Number.isFinite(value) && value > 0 && value <= 100_000 ? value : null; }
function normalizedText(value: unknown, max: number) { return clean(value, max).toLocaleLowerCase(); }
function normalizedUnit(value: unknown) { const unit = normalizedText(value, 80).replace(/\.$/, ""); return unit ? UNIT_NORMALIZATION[unit] ?? unit : null; }
const comparableUnits = {
  tsp: ["volume", 4.92892], tbsp: ["volume", 14.7868], cup: ["volume", 236.588],
  "fl oz": ["volume", 29.5735], ml: ["volume", 1], l: ["volume", 1000],
  pt: ["volume", 473.176], qt: ["volume", 946.353], gal: ["volume", 3785.41],
  g: ["weight", 1], kg: ["weight", 1000], oz: ["weight", 28.3495], lb: ["weight", 453.592],
} as const;
const unicodeFractions: Record<string, number> = { "⅛": 0.125, "¼": 0.25, "⅓": 1 / 3, "½": 0.5, "⅔": 2 / 3, "¾": 0.75 };
const knownUnitAliases = Object.keys(UNIT_NORMALIZATION).sort((left, right) => right.length - left.length);

function enforceComparableQuantityOrder(ingredients: ParsedIngredient[]) {
  const ordered = [...ingredients];
  for (const dimension of ["volume", "weight"] as const) {
    const entries = ordered.flatMap((ingredient, index) => {
      const conversion = ingredient.unit ? comparableUnits[ingredient.unit as keyof typeof comparableUnits] : undefined;
      return conversion?.[0] === dimension && ingredient.amount !== null
        ? [{ ingredient, index, quantity: ingredient.amount * conversion[1] }]
        : [];
    });
    const slots = entries.map(entry => entry.index);
    entries.sort((left, right) => right.quantity - left.quantity || left.index - right.index);
    slots.forEach((slot, index) => { ordered[slot] = entries[index]!.ingredient; });
  }
  return ordered;
}

function splitQualifiers(value: string) {
  const [foodName, ...commaQualifiers] = value.split(",");
  if (commaQualifiers.length) return { foodName: (foodName ?? "").trim(), qualifiers: commaQualifiers.map(item => item.trim()).filter(Boolean) };
  const match = /^(.*?)(?:\s+(to taste|as needed|for serving|for garnish))$/i.exec(value);
  return match ? { foodName: match[1]!.trim(), qualifiers: [match[2]!] } : { foodName: value.trim(), qualifiers: [] };
}

function isSecondaryMeasurement(value: string) {
  return /^(?:about\s+)?\(?\d+(?:\.\d+)?\s*(?:g|kg|ml|l|oz|lb|fl\s*oz)\)?$/i.test(value.trim());
}

function normalizedQualifiers(values: unknown[]) {
  return values.map(item => normalizedText(item, 120).replace(/^,\s*/, "")).filter(Boolean).filter(item => !isSecondaryMeasurement(item)).slice(0, 20);
}

/** Conservative compatibility splitter for older metadata and provider line arrays. */
function legacyIngredient(value: string) {
  const line = clean(value, RECIPE_TEXT_MAX);
  const match = /^(?:(\d+(?:\.\d+)?)(?:\s+(\d+)\/(\d+)|\s*([⅛¼⅓½⅔¾]))?|([⅛¼⅓½⅔¾]))\s+(.+)$/.exec(line);
  if (!match) {
    const parts = splitQualifiers(line);
    return { amount: null, unit: null, foodName: parts.foodName, qualifiers: parts.qualifiers };
  }
  const whole = match[1] ? Number(match[1]) : 0;
  const fraction = match[2] && match[3] ? Number(match[2]) / Number(match[3]) : unicodeFractions[match[4] ?? match[5] ?? ""] ?? 0;
  const rest = match[6]!.trim(); const lower = rest.toLocaleLowerCase();
  const alias = knownUnitAliases.find(candidate => lower === candidate || lower.startsWith(`${candidate} `));
  const unit = alias ? UNIT_NORMALIZATION[alias]! : null;
  const parts = splitQualifiers(alias ? rest.slice(alias.length).trim() : rest);
  return { amount: amount(whole + fraction), unit, foodName: parts.foodName, qualifiers: parts.qualifiers };
}

export function formatIngredientAmount(value: number) {
  const whole = Math.floor(value);
  const fraction = value - whole;
  const candidates: Array<[number, string]> = [[0.125, "⅛"], [0.25, "¼"], [1 / 3, "⅓"], [0.5, "½"], [2 / 3, "⅔"], [0.75, "¾"]];
  const match = candidates.find(([number]) => Math.abs(fraction - number) <= COOKING_FRACTION_TOLERANCE);
  if (match) return `${whole || ""}${match[1]}`;
  if (fraction <= COOKING_FRACTION_TOLERANCE) return String(whole);
  return value.toFixed(3).replace(/\.?(?:0+)$/, "");
}

/** Rebuilds an AI ingredient line from structured fields; displayLine is never trusted. */
export function formatIngredient(input: Omit<ParsedIngredient, "displayLine">) {
  const foodName = normalizedText(input.foodName, 500);
  const qualifiers = normalizedQualifiers(input.qualifiers);
  if (!foodName) return "";
  const quantity = amount(input.amount);
  const unit = quantity === null ? null : normalizedUnit(input.unit);
  const displayUnit = unit === "tbsp" ? "Tbsp" : unit;
  const prefix = quantity === null ? "" : `${formatIngredientAmount(quantity)}${displayUnit ? ` ${displayUnit}` : ""} `;
  return `${prefix}${foodName}${qualifiers.length ? `, ${qualifiers.join(", ")}` : ""}`;
}

/** Standardizes tablespoon spellings in stored or manually edited ingredient text. */
export function normalizeRecipeIngredientText(value: string) {
  return value.replace(/\b(?:tablespoons?|tbsp)\.?(?=\s|$)/giu, "Tbsp");
}

function estimatedNutrition(value: unknown): EstimatedNutrition | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const input = value as Record<string, unknown>;
  const number = (candidate: unknown, maximum: number) => typeof candidate === "number" && Number.isFinite(candidate) && candidate >= 0 && candidate <= maximum ? candidate : null;
  const calories = number(input.calories, 1_000_000); const protein = number(input.protein, 100_000); const carbohydrates = number(input.carbohydrates, 100_000); const fat = number(input.fat, 100_000);
  return calories === null || protein === null || carbohydrates === null || fat === null ? null : { calories, protein, carbohydrates, fat };
}

/** Returns a normalized parse or an honest warning; invalid provider data is never persisted as a recipe parse. */
export function normalizeParsedRecipe(value: unknown): { recipe: ParsedRecipe | null; warning: RecipeParseWarning | null } {
  if (!value || typeof value !== "object" || Array.isArray(value)) return { recipe: null, warning: { code: "invalid_parse", message: "Recipe details could not be read. Your original recipe text was saved." } };
  const input = value as Record<string, unknown>;
  const name = clean(input.name, RECIPE_NAME_MAX); const rawIngredients = input.ingredients; const rawInstructions = input.instructions;
  if (!name || !Array.isArray(rawIngredients) || !Array.isArray(rawInstructions)) return { recipe: null, warning: { code: "invalid_parse", message: "Recipe details could not be read. Your original recipe text was saved." } };
  const ingredients: ParsedIngredient[] = [];
  for (const candidate of rawIngredients.slice(0, MAX_ITEMS)) {
    if (typeof candidate === "string") {
      const legacy = legacyIngredient(candidate); const foodName = normalizedText(legacy.foodName, 500);
      const qualifiers = normalizedQualifiers(legacy.qualifiers);
      const parsedAmount = amount(legacy.amount); const parsedUnit = parsedAmount === null ? null : normalizedUnit(legacy.unit);
      if (foodName) ingredients.push({ displayLine: formatIngredient({ amount: parsedAmount, unit: parsedUnit, foodName, qualifiers }), amount: parsedAmount, unit: parsedUnit, foodName, qualifiers });
      continue;
    }
    if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) continue;
    const item = candidate as Record<string, unknown>;
    const legacy = legacyIngredient(clean(item.displayLine ?? item.line ?? item.ingredient, RECIPE_TEXT_MAX));
    const foodName = normalizedText(item.foodName, 500) || normalizedText(legacy.foodName, 500);
    if (!foodName) continue;
    const parsedAmount = amount(item.amount) ?? (item.amount === undefined ? amount(legacy.amount) : null); const parsedUnit = parsedAmount === null ? null : normalizedUnit(item.unit ?? legacy.unit);
    const qualifiers = Array.isArray(item.qualifiers) ? normalizedQualifiers(item.qualifiers) : normalizedQualifiers(legacy.qualifiers);
    const displayLine = formatIngredient({ amount: parsedAmount, unit: parsedUnit, foodName, qualifiers });
    if (displayLine) ingredients.push({ displayLine, amount: parsedAmount, unit: parsedUnit, foodName, qualifiers });
  }
  const instructions = rawInstructions.slice(0, MAX_ITEMS).map(item => item && typeof item === "object" && !Array.isArray(item) ? clean((item as Record<string, unknown>).text ?? (item as Record<string, unknown>).name, RECIPE_TEXT_MAX) : clean(item, RECIPE_TEXT_MAX)).filter(Boolean);
  if (!ingredients.length || !instructions.length) return { recipe: null, warning: { code: "incomplete_parse", message: "Some recipe details could not be read. Your original recipe text was saved for editing." } };
  return { recipe: { version: RECIPE_PARSE_SCHEMA_VERSION, name, servings: amount(input.servings) ?? 1, ingredients: enforceComparableQuantityOrder(ingredients), instructions, estimatedNutrition: estimatedNutrition(input.estimatedNutrition) }, warning: null };
}

export function parsedRecipeText(recipe: ParsedRecipe) {
  return { ingredients: recipe.ingredients.map(item => item.displayLine).join("\n"), instructions: recipe.instructions.join("\n") };
}

export function recipeEditorText(recipe: Pick<ParsedRecipe, "servings" | "ingredients" | "instructions">) {
  return formatRecipeEditorText({ servings: recipe.servings, ingredients: parsedRecipeText(recipe as ParsedRecipe).ingredients, instructions: recipe.instructions.join("\n") });
}

export function formatRecipeEditorText(recipe: Pick<ParsedRecipe, "servings"> & { ingredients: string; instructions: string }) {
  return `Servings: ${recipe.servings}\n\nIngredients\n${normalizeRecipeIngredientText(recipe.ingredients)}\n\nInstructions\n${recipe.instructions}`;
}
